import { ReactElement, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  Input,
  PrimaryButton,
  SecondaryButton,
  SnackbarStatus,
  StatusBar,
  Typography,
} from '@weareopenr/component-library';
import { useTimetables } from 'src/hooks/useTimetables';
import { useUpdateTimetableDetails } from 'src/hooks/useUpdateTimetableDetails';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { DUPLICATE_NAME_ERROR } from '../CreateTimetableModal/fields/TimetableNameField';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import { ICloseRenameTrayOptions, IRenameTimetableFormValues } from './types';
import styles from './RenameTimetableTrayContent.module.scss';

const EXISTING_NAMES_STALE_TIME = 5 * 60 * 1000;

export interface RenameTimetableTrayContentProps {
  timetableId: string;
  currentName: string;
  onClose: (options?: ICloseRenameTrayOptions) => void;
  openId: number;
}

export const RenameTimetableTrayContent = ({
  timetableId,
  currentName,
  onClose,
  openId,
}: RenameTimetableTrayContentProps): ReactElement => {
  const { mutateAsync: updateTimetableDetails } = useUpdateTimetableDetails(timetableId);
  const { trackEvent, captureException } = useUserTracking();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { data: timetablesData } = useTimetables(15, undefined, undefined, { staleTime: EXISTING_NAMES_STALE_TIME });
  // Excludes the timetable being renamed, so changing only the casing of its own name is allowed -
  // matching the backend's uniqueness check.
  const existingTimetableNames = useMemo(
    () =>
      timetablesData?.pages.flatMap((page) =>
        page.data.filter((timetable) => timetable.timetable_id !== timetableId).map((timetable) => timetable.name),
      ) ?? [],
    [timetablesData, timetableId],
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isValid },
  } = useForm<IRenameTimetableFormValues>({
    mode: 'onChange',
    defaultValues: { name: currentName },
  });

  useEffect(() => {
    reset({ name: currentName });
    clearError();
  }, [openId]);

  // Names created before the API trimmed them can carry surrounding whitespace, and the
  // rename trims, so compare both sides trimmed or Save would enable on an untouched form.
  const isUnchanged = watch('name').trim() === currentName.trim();

  const onSubmit = async (values: IRenameTimetableFormValues) => {
    clearError();

    try {
      await updateTimetableDetails({ name: values.name.trim() });

      trackEvent('timetable_renamed', { timetableId });
      showNotification({ status: SnackbarStatus.SUCCESS, message: 'Timetable name updated' });
      onClose({ renamed: true });
    } catch (error) {
      captureException(error, { tags: { action: 'timetable_rename' } });
      const message = error instanceof Error ? error.message : undefined;
      showNotification({
        status: SnackbarStatus.ERROR,
        message: message || 'Failed to update timetable name. Please try again.',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['content']}>
      <div>
        <Typography size="medium" weight="regular" className={styles['subtitle']}>
          Current name - <strong>{currentName}</strong>
        </Typography>

        <Input
          type="text"
          label="Timetable Name (internal only)"
          register={register('name', {
            required: 'Please enter a timetable name',
            validate: {
              notEmpty: (value) => value.trim().length > 0 || 'Please enter a timetable name',
              isUnique: (value) => {
                const trimmedValue = value.trim().toLocaleLowerCase();
                const isDuplicate = existingTimetableNames.some((name) => name.toLocaleLowerCase() === trimmedValue);
                return !isDuplicate || DUPLICATE_NAME_ERROR;
              },
            },
          })}
          error={errors?.name?.message}
        />
      </div>

      {errorMessage && <StatusBar message={errorMessage} variant="error" />}

      <div className={styles['button-row']}>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <SecondaryButton buttonStyle="outline" type="button" size="full-width" onClick={() => onClose()}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              type="submit"
              size="full-width"
              loading={isSubmitting}
              disabled={isSubmitting || !isValid || isUnchanged}
            >
              Save
            </PrimaryButton>
          }
        />
      </div>
    </form>
  );
};

export default RenameTimetableTrayContent;
